import React, { useEffect, useRef, useState } from "react";
import { FileText, Sparkles, UploadCloud, Loader2, Trash2, Send, X, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button, Pill } from "./Primitives";

const MAX_BYTES = 20 * 1024 * 1024;

export default function AdminAssignments() {
  const fileRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [doc, setDoc] = useState(null); // { filename, text, char_count, truncated }
  const [title, setTitle] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null); // { questions:[], writing_task:{} }
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState([]);

  const loadPublished = () => api.get("/admin/coursework").then((r) => setPublished(r.data)).catch(() => {});
  useEffect(() => { loadPublished(); }, []);

  const handleFile = async (file) => {
    if (!file) return;
    const okType = file.type === "application/pdf" || file.type.startsWith("text/") ||
      /\.(pdf|txt|md|csv)$/i.test(file.name);
    if (!okType) return toast.error("Please upload a PDF or text file");
    if (file.size > MAX_BYTES) return toast.error("File too large (max 20MB)");
    setExtracting(true); setResult(null); setDoc(null);
    try {
      const res = await api.post("/admin/coursework/extract", file, {
        headers: { "Content-Type": file.type || "application/octet-stream", "X-Filename": file.name },
      });
      setDoc(res.data);
      setTitle(file.name.replace(/\.[^.]+$/, ""));
      toast.success("Text extracted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that file");
    } finally {
      setExtracting(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const generate = async () => {
    if (!title.trim()) return toast.error("Add a title first");
    setGenerating(true);
    try {
      const res = await api.post("/admin/coursework/generate", { title: title.trim(), source_text: doc.text });
      setResult(res.data);
      toast.success("Assignments generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await api.post("/admin/coursework", {
        title: title.trim(),
        source_filename: doc?.filename || "",
        questions: result.questions,
        writing_task: result.writing_task,
      });
      toast.success("Assignment published for students");
      setDoc(null); setResult(null); setTitle("");
      loadPublished();
    } catch {
      toast.error("Could not publish");
    } finally {
      setPublishing(false);
    }
  };

  const removePublished = async (id) => {
    await api.delete(`/admin/coursework/${id}`);
    toast("Assignment removed");
    loadPublished();
  };

  const updateQuestion = (i, field, val) => {
    setResult((r) => {
      const questions = r.questions.map((q, idx) => (idx === i ? { ...q, [field]: val } : q));
      return { ...r, questions };
    });
  };
  const updateWriting = (field, val) => setResult((r) => ({ ...r, writing_task: { ...r.writing_task, [field]: val } }));

  const input = "w-full border border-edge bg-ink px-4 py-3 text-bone placeholder:text-muted focus:border-gold focus:outline-none";

  return (
    <div className="mt-10 grid gap-12 lg:grid-cols-12" data-testid="admin-assignments">
      {/* LEFT: upload + preview + generate */}
      <div className="lg:col-span-7">
        <input ref={fileRef} type="file" accept=".pdf,.txt,.md,.csv,application/pdf,text/plain" className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])} data-testid="doc-file-input" />

        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid="doc-dropzone"
          className={`flex cursor-pointer flex-col items-center justify-center border-2 border-dashed p-10 text-center transition-colors ${dragging ? "border-gold bg-gold/5" : "border-edge bg-ash hover:border-gold/50"}`}
        >
          {extracting ? (
            <><Loader2 className="h-8 w-8 animate-spin text-gold" /><p className="mt-3 text-muted">Extracting text…</p></>
          ) : (
            <>
              <UploadCloud className="h-8 w-8 text-gold" />
              <p className="mt-3 text-bone">Drag & drop a PDF or text file</p>
              <p className="mt-1 text-xs text-muted">or click to browse · max 20MB</p>
            </>
          )}
        </div>

        {doc && (
          <div className="mt-6" data-testid="doc-preview">
            <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.15em] text-gold">Assignment title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="doc-title-input" className={`${input} mb-4`} />

            <div className="mb-3 flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-muted"><FileText className="h-4 w-4" /> {doc.filename} · {doc.char_count.toLocaleString()} chars{doc.truncated ? " (preview)" : ""}</span>
              <button onClick={() => { setDoc(null); setResult(null); }} className="text-muted hover:text-blood" data-testid="doc-clear-btn"><X className="h-4 w-4" /></button>
            </div>
            <div className="max-h-72 overflow-y-auto whitespace-pre-line border border-edge bg-ink p-4 text-sm leading-relaxed text-[#cfc9bd]" data-testid="doc-preview-text">
              {doc.text}
            </div>

            <Button variant="gold" onClick={generate} disabled={generating} className="mt-5 w-full" data-testid="generate-assignments-btn">
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate Assignments
            </Button>
          </div>
        )}

        {result && (
          <div className="mt-8 border border-edge bg-ash p-6" data-testid="generated-result">
            <h3 className="mb-4 font-serif text-2xl text-bone">Review & edit</h3>
            {result.questions.map((q, i) => (
              <div key={i} className="mb-4" data-testid={`gen-question-${i}`}>
                <label className="mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-gold">Question {i + 1} (short answer)</label>
                <textarea value={q.question} onChange={(e) => updateQuestion(i, "question", e.target.value)} rows={2} className={`${input} mb-2`} />
                <input value={q.model_answer} onChange={(e) => updateQuestion(i, "model_answer", e.target.value)} placeholder="Model answer (educator only)" className={`${input} text-sm`} />
              </div>
            ))}
            <div className="mb-4" data-testid="gen-writing-task">
              <label className="mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-gold">Writing task</label>
              <textarea value={result.writing_task.prompt} onChange={(e) => updateWriting("prompt", e.target.value)} rows={2} className={`${input} mb-2`} />
              <input value={result.writing_task.guidance} onChange={(e) => updateWriting("guidance", e.target.value)} placeholder="Guidance for a strong response" className={`${input} text-sm`} />
            </div>
            <Button variant="gold" onClick={publish} disabled={publishing} className="w-full" data-testid="publish-assignment-btn">
              {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Publish for Students
            </Button>
          </div>
        )}
      </div>

      {/* RIGHT: published list */}
      <div className="lg:col-span-5">
        <h2 className="mb-5 font-serif text-2xl text-bone">Published Assignments ({published.length})</h2>
        {published.length === 0 ? (
          <p className="border border-dashed border-edge p-10 text-center text-muted" data-testid="assignments-empty">No assignments published yet.</p>
        ) : (
          <div className="space-y-3">
            {published.map((c) => (
              <div key={c.id} className="border border-edge bg-ash p-4" data-testid={`published-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-bone">{c.title}</p>
                    <p className="mt-0.5 text-xs text-muted">{c.questions?.length || 0} questions + 1 writing task{c.source_filename ? ` · ${c.source_filename}` : ""}</p>
                  </div>
                  <button onClick={() => removePublished(c.id)} data-testid={`delete-published-${c.id}`} className="text-muted hover:text-blood"><Trash2 className="h-4 w-4" /></button>
                </div>
                <a href={`/assignments/${c.slug}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-gold hover:underline">
                  <Plus className="h-3 w-3" /> View student page
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
